/**
 * Syntax API Service - Cliente para endpoints de Sintaxis Espacial
 *
 * Este servicio proporciona acceso a los endpoints de cálculo de
 * Space Syntax en el backend.
 *
 * @example
 * ```typescript
 * // Calcular sintaxis
 * const result = await syntaxApi.calculate(projectId, 800);
 *
 * // Obtener métricas
 * const { edges, hasMetrics } = await syntaxApi.getMetrics(projectId);
 *
 * // Obtener rangos para normalizar colores
 * const range = await syntaxApi.getRange(projectId);
 * ```
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (!authStorage) return null;
    const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
    return parsed.state?.token || null;
  } catch {
    return null;
  }
};

/**
 * Crea headers con autenticación
 */
const getHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Estados posibles del pipeline de sintaxis
 */
export type SyntaxStatusType = 'pending' | 'calculating' | 'valid' | 'stale' | 'error';

/**
 * Resultado del cálculo de sintaxis
 */
export interface SyntaxCalculationResult {
  edgesProcessed: number;
  computationTimeMs: number;
  localRadiusM: number;
  nodeCount: number;
  syntaxVersion: number;
  syntaxStatus: SyntaxStatusType;
  syntaxBasedOnNetworkVersion: number;
}

/**
 * Métrica de sintaxis para un edge (multi-radio)
 * @see PLAN_SINTAXIS_ESPACIAL_PIPELINE.md
 */
export interface SyntaxMetric {
  edgeId: string;
  // Integration metrics (4 radios: global, global_raw, r1200, r800)
  integrationGlobal: number | null;
  integrationGlobalRaw: number | null;
  integrationR1200: number | null;
  integrationR800: number | null;
  // Choice metrics (3 radios: global, r1200, r800)
  choiceGlobal: number | null;
  choiceR1200: number | null;
  choiceR800: number | null;
}

/**
 * Respuesta de métricas de sintaxis
 */
export interface SyntaxMetricsResponse {
  edges: SyntaxMetric[];
  calculatedAt: string | null;
  hasMetrics: boolean;
  // Pipeline version info
  syntaxVersion: number;
  syntaxStatus: SyntaxStatusType;
  syntaxBasedOnNetworkVersion: number | null;
  urbanNetworkVersion: number;
  isStale: boolean;
}

/**
 * Rango de valores para una métrica
 */
export interface MetricRange {
  min: number;
  max: number;
}

/**
 * Rangos de todas las métricas (multi-radio)
 * @see PLAN_SINTAXIS_ESPACIAL_PIPELINE.md
 */
export interface SyntaxRange {
  // Integration ranges (4 radios: global, global_raw, r1200, r800)
  integrationGlobal: MetricRange;
  integrationGlobalRaw: MetricRange;
  integrationR1200: MetricRange;
  integrationR800: MetricRange;
  // Choice ranges (3 radios: global, r1200, r800)
  choiceGlobal: MetricRange;
  choiceR1200: MetricRange;
  choiceR800: MetricRange;
}

/**
 * Estado del syntax engine
 */
export interface SyntaxEngineHealth {
  available: boolean;
  url: string;
}

/**
 * Claves de métricas válidas para carga slim — deben coincidir con el backend
 */
export type SyntaxMetricSlimKey =
  | 'integrationGlobal'
  | 'integrationGlobalRaw'
  | 'integrationR1200'
  | 'integrationR800'
  | 'choiceGlobal'
  | 'choiceR1200'
  | 'choiceR800';

/**
 * Mapeo de SyntaxMetricType (snake_case del contexto) a SyntaxMetricSlimKey (camelCase del API)
 */
export const METRIC_TYPE_TO_SLIM_KEY: Record<string, SyntaxMetricSlimKey> = {
  integration_global: 'integrationGlobal',
  integration_global_raw: 'integrationGlobalRaw',
  integration_r1200: 'integrationR1200',
  integration_r800: 'integrationR800',
  choice_global: 'choiceGlobal',
  choice_r1200: 'choiceR1200',
  choice_r800: 'choiceR800',
};

/**
 * Edge con valor único de métrica — respuesta slim del backend
 */
export interface SyntaxMetricSlimEdge {
  edgeId: string;
  value: number | null;
}

/**
 * Respuesta slim: una sola métrica por edge (~7x menor que SyntaxMetricsResponse)
 */
export interface SyntaxMetricSlimResponse {
  metric: SyntaxMetricSlimKey;
  edges: SyntaxMetricSlimEdge[];
  hasMetrics: boolean;
}

/**
 * Estado del pipeline de sintaxis
 * Incluye información de versionado para detectar si está desactualizado
 */
export interface SyntaxPipelineStatus {
  syntaxStatus: SyntaxStatusType;
  syntaxVersion: number;
  syntaxCalculatedAt: string | null;
  syntaxBasedOnNetworkVersion: number | null;
  urbanNetworkVersion: number;
  isStale: boolean;
}

/**
 * Respuesta de invalidación de sintaxis
 */
export interface SyntaxInvalidateResponse {
  message: string;
  status: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

export const syntaxApi = {
  /**
   * Encola recálculo de sintaxis espacial para un proyecto
   *
   * @param projectId - ID del proyecto
   * @returns Job ID y mensaje de confirmación
   */
  async recalculate(projectId: string): Promise<{ jobId: string; message: string }> {
    const response = await axios.post<{ jobId: string; message: string }>(
      `${API_URL}/projects/${projectId}/syntax/recalculate`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Obtiene métricas de sintaxis calculadas
   *
   * @param projectId - ID del proyecto
   * @returns Métricas por edge + metadata
   */
  async getMetrics(projectId: string): Promise<SyntaxMetricsResponse> {
    const response = await axios.get<SyntaxMetricsResponse>(
      `${API_URL}/projects/${projectId}/syntax/metrics`,
      { headers: getHeaders(), timeout: 45000 } // 45s — puede traer 37K registros
    );
    return response.data;
  },

  /**
   * Obtiene una sola métrica de sintaxis por edge (respuesta liviana)
   *
   * Llama a GET /syntax/metrics?metric=<slimKey> y devuelve solo { edgeId, value }
   * para cada edge. El payload es ~7x menor que getMetrics().
   *
   * @param projectId  - ID del proyecto
   * @param metricType - Tipo de métrica en formato SyntaxMetricType (snake_case)
   * @returns Respuesta slim con { metric, edges, hasMetrics }
   */
  async getMetricSlim(projectId: string, metricType: string): Promise<SyntaxMetricSlimResponse> {
    const slimKey = METRIC_TYPE_TO_SLIM_KEY[metricType];
    if (!slimKey) {
      throw new Error(`Unknown metric type: ${metricType}`);
    }
    const response = await axios.get<SyntaxMetricSlimResponse>(
      `${API_URL}/projects/${projectId}/syntax/metrics`,
      {
        params: { metric: slimKey },
        headers: getHeaders(),
        timeout: 15000, // slim — mucho más rápido que el full (45s)
      }
    );
    return response.data;
  },

  /**
   * Obtiene rangos min/max de métricas
   *
   * @param projectId - ID del proyecto
   * @returns Rangos para normalizar colores
   */
  async getRange(projectId: string): Promise<SyntaxRange> {
    const response = await axios.get<SyntaxRange>(`${API_URL}/projects/${projectId}/syntax/range`, {
      headers: getHeaders(),
      timeout: 15000,
    });
    return response.data;
  },

  /**
   * Verifica si el syntax engine está disponible
   *
   * @returns Estado del servicio
   */
  async checkHealth(): Promise<SyntaxEngineHealth> {
    try {
      const response = await axios.get<SyntaxEngineHealth>(`${API_URL}/syntax/health`, {
        headers: getHeaders(),
        timeout: 5000,
      });
      return response.data;
    } catch {
      return { available: false, url: 'unknown' };
    }
  },

  /**
   * Obtiene el estado del pipeline de sintaxis
   *
   * @param projectId - ID del proyecto
   * @returns Estado del pipeline incluyendo info de versionado
   */
  async getStatus(projectId: string): Promise<SyntaxPipelineStatus> {
    const response = await axios.get<SyntaxPipelineStatus>(
      `${API_URL}/projects/${projectId}/syntax/status`,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Marca la sintaxis como desactualizada y dispara recálculo en background
   *
   * @param projectId - ID del proyecto
   * @returns Mensaje de confirmación
   */
  async invalidate(projectId: string): Promise<SyntaxInvalidateResponse> {
    const response = await axios.post<SyntaxInvalidateResponse>(
      `${API_URL}/projects/${projectId}/syntax/invalidate`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },
};
